import { toBase64, fromBase64 } from "lib0/buffer"
import type { Transport } from "./yjs-provider"

// Daily's `sendAppMessage` serializes its payload to JSON and caps the size
// (a few KB). Yjs messages are binary and can exceed that (notably the initial
// state sync), so we split each message into base64 chunks under the cap and
// reassemble on the far side. Incremental edits are tiny and ride in one chunk.
//
// Raw bytes per chunk. base64 inflates ~33% (2 KB -> ~2.7 KB) plus the small
// JSON envelope, leaving comfortable headroom under Daily's limit.
const MAX_CHUNK_BYTES = 2048

// Guard against unbounded memory if a peer leaves mid-message and its chunks
// never complete: cap the number of in-flight reassemblies.
const MAX_PENDING_REASSEMBLIES = 256

const ENVELOPE_TAG = "ydoc"

export interface ChunkEnvelope {
  t: typeof ENVELOPE_TAG
  // Sender transport "epoch" — distinguishes message-id streams from the SAME
  // sender across a reconnect (which rebuilds the transport and resets `id` to 0),
  // so a stale partial reassembly can't alias a new message. See handleIncoming.
  e: number
  // message id, unique per sender+epoch
  id: number
  // chunk index
  i: number
  // total chunks
  n: number
  // base64 chunk data
  d: string
}

function isChunkEnvelope(value: unknown): value is ChunkEnvelope {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  // Range-validate, not just type-check: a peer claiming n <= 0 (or i out of
  // range) must be rejected, or we'd hand an empty buffer to the Yjs decoder and
  // crash the message pump. We only ever emit n >= 1 ourselves.
  return (
    v.t === ENVELOPE_TAG &&
    typeof v.e === "number" &&
    typeof v.id === "number" &&
    typeof v.d === "string" &&
    Number.isInteger(v.n) &&
    (v.n as number) >= 1 &&
    Number.isInteger(v.i) &&
    (v.i as number) >= 0 &&
    (v.i as number) < (v.n as number)
  )
}

function concatChunks(parts: Uint8Array[]): Uint8Array {
  let total = 0
  for (const part of parts) total += part.length
  const out = new Uint8Array(total)
  let offset = 0
  for (const part of parts) {
    out.set(part, offset)
    offset += part.length
  }
  return out
}

interface PendingReassembly {
  total: number
  parts: Array<Uint8Array | undefined>
  received: number
}

// A {@link Transport} over Daily's `app-message` data channel. The React layer
// supplies `send` (`daily.sendAppMessage`) and pumps inbound events through
// `handleIncoming`; this class owns only chunking + reassembly, which keeps it
// pure and unit-testable without a Daily call object.
// Distinguishes successive transport instances within one page (each construct
// gets a fresh epoch). Combined with the sender's Daily session id, it makes a
// reconnected sender's reset message-id stream unambiguous to the receiver.
let transportEpochCounter = 0

export class DailyTransport implements Transport {
  private handler: ((bytes: Uint8Array, fromId: string) => void) | null = null
  private readonly pending = new Map<string, PendingReassembly>()
  private sequence = 0
  private readonly epoch = transportEpochCounter++

  constructor(private readonly send: (data: ChunkEnvelope) => void) {}

  broadcast(bytes: Uint8Array): void {
    const id = this.sequence++
    const total = Math.max(1, Math.ceil(bytes.length / MAX_CHUNK_BYTES))
    for (let i = 0; i < total; i++) {
      const chunk = bytes.subarray(i * MAX_CHUNK_BYTES, (i + 1) * MAX_CHUNK_BYTES)
      this.send({ t: ENVELOPE_TAG, e: this.epoch, id, i, n: total, d: toBase64(chunk) })
    }
  }

  subscribe(handler: (bytes: Uint8Array, fromId: string) => void): () => void {
    this.handler = handler
    return () => {
      if (this.handler === handler) this.handler = null
    }
  }

  // Feed one inbound `app-message` payload. `fromId` is the Daily session id of
  // the sender, used to namespace message ids so two peers' streams can't
  // collide. Non-Yjs app-messages (anything we didn't send) are ignored.
  handleIncoming(data: unknown, fromId: string): void {
    if (!isChunkEnvelope(data)) return

    // Key on sender + epoch + id, so a reconnected sender's reset id stream can't
    // alias a stale partial reassembly from before the reconnect.
    const key = `${fromId}:${data.e}:${data.id}`
    let entry = this.pending.get(key)
    // A mismatched total for the same key means this isn't the same message
    // (corrupt or duplicated id) — drop the stale partial and start fresh.
    if (entry && entry.total !== data.n) {
      this.pending.delete(key)
      entry = undefined
    }
    if (!entry) {
      // Evict the OLDEST incomplete reassembly (Map preserves insertion order)
      // instead of clearing them all, so one abandoned partial can't wipe a
      // genuinely in-flight message.
      if (this.pending.size >= MAX_PENDING_REASSEMBLIES) {
        const oldest = this.pending.keys().next().value
        if (oldest !== undefined) this.pending.delete(oldest)
      }
      entry = { total: data.n, parts: new Array(data.n), received: 0 }
      this.pending.set(key, entry)
    }

    if (data.i >= 0 && data.i < entry.total && entry.parts[data.i] === undefined) {
      entry.parts[data.i] = fromBase64(data.d)
      entry.received++
    }

    if (entry.received === entry.total) {
      this.pending.delete(key)
      // A malformed/incompatible buffer must not throw out of the Daily event
      // handler (which would kill the pump) — drop it; the 5s resync recovers.
      try {
        this.handler?.(concatChunks(entry.parts as Uint8Array[]), fromId)
      } catch {
        /* corrupt Yjs payload — drop */
      }
    }
  }
}
